import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminEditModeProvider } from "@/contexts/AdminEditModeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useEmailNotifications } from "@/hooks/useEmailNotifications";
import ScrollToTop from "@/components/ScrollToTop";
import { useAdminTheme } from "@/hooks/useAdminTheme";
import { lazy, Suspense, useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { appRoutes } from "@/routes";
import { CookieConsent } from "@/components/CookieConsent";
import { PageTransition } from "@/components/motion/PageTransition";
import Lenis from "lenis";

const AdminEditToolbar = lazy(() => import("@/components/admin/AdminEditToolbar").then(m => ({ default: m.AdminEditToolbar })));
const KeyboardShortcutsDialog = lazy(() => import("@/components/KeyboardShortcutsDialog").then(m => ({ default: m.KeyboardShortcutsDialog })));
const VideoCallNotification = lazy(() => import("@/components/VideoCallNotification").then(m => ({ default: m.VideoCallNotification })));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent = () => {
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useKeyboardShortcuts(
    () => setSearchOpen(true),
    () => setShortcutsOpen(true)
  );

  useEmailNotifications();
  useAdminTheme();

  return (
    <>
      <ScrollToTop />
      <PageTransition>
        <Suspense fallback={<PageLoader />}>
          <Routes>{appRoutes}</Routes>
        </Suspense>
      </PageTransition>
      <Sonner />
      <CookieConsent />
      <Suspense fallback={null}>
        <VideoCallNotification />
      </Suspense>
      <Suspense fallback={null}>
        <AdminEditToolbar />
      </Suspense>
      <TooltipProvider>
        <Suspense fallback={null}>
          <KeyboardShortcutsDialog 
            open={shortcutsOpen}
            onOpenChange={setShortcutsOpen}
          />
        </Suspense>
      </TooltipProvider>
    </>
  );
};

const App = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      touchMultiplier: 2,
      infinite: false,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <AuthProvider>
                <AdminEditModeProvider>
                  <AppContent />
                </AdminEditModeProvider>
              </AuthProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
